import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getPlatformProfiles,
  getPlatformSchemas,
  getPlatformScoring,
  type DbPlatformProfile,
  type DbPlatformSchema,
  type DbPlatformScoring,
} from '@/api/astraea';

const PLATFORM_LABEL: Record<string, string> = {
  xiaohongshu: '小红书',
  douyin: '抖音',
  kuaishou: '快手',
  bilibili: 'B站',
  wechat_channels: '微信视频号',
};

function Chips({ items, color }: { items: string[] | null | undefined; color: string }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {(items ?? []).map((t, i) => (
        <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${color}`}>
          {t}
        </span>
      ))}
    </div>
  );
}

export default function PlatformsPage() {
  const [profiles, setProfiles] = useState<DbPlatformProfile[]>([]);
  const [schemas, setSchemas] = useState<DbPlatformSchema[]>([]);
  const [scoring, setScoring] = useState<DbPlatformScoring[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getPlatformProfiles(), getPlatformSchemas(), getPlatformScoring()])
      .then(([p, s, sc]) => {
        setProfiles(p);
        setSchemas(s);
        setScoring(sc);
      })
      .catch((e) => setError(e instanceof Error ? e.message : '读取失败'))
      .finally(() => setLoading(false));
  }, []);

  const schemaOf = (platform: string) => schemas.find((s) => s.platform === platform);
  const scoringOf = (platform: string) => scoring.find((s) => s.platform === platform);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-xs text-secondary-400 mb-2">
          <Link to="/board" className="hover:text-foreground-600">角色</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span>平台字典</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">平台商业策略字典</h1>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-3xl">
          各平台的商业定位、流量逻辑、适合 / 不适合角色、风险规则、转化方式、核心验证指标与素材格式。
          策略大脑与素材生成会引用这些设定,让每个平台「各产各的」,不一稿多发。
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1.5"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-sm text-secondary-400">
          <div className="inline-block w-6 h-6 border-2 border-secondary-200 border-t-primary-500 rounded-full animate-spin mb-3"></div>
          <div>读取中…</div>
        </div>
      ) : (
        <div className="space-y-6">
          {profiles.map((p) => {
            const sc = schemaOf(p.platform);
            const sr = scoringOf(p.platform);
            return (
              <div key={p.id} className="bg-background-100 rounded-xl border border-secondary-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-sm font-semibold">
                    {PLATFORM_LABEL[p.platform] ?? p.platform}
                  </span>
                  <span className="text-xs text-secondary-400">{p.commercial_positioning}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-secondary-500 mb-1">流量逻辑</div>
                    <div className="text-foreground-700 leading-relaxed">{p.traffic_logic}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-secondary-500 mb-1">转化方式</div>
                    <div className="text-foreground-700 leading-relaxed">{p.conversion_method}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-secondary-500 mb-1.5">商业模式</div>
                    <Chips items={p.business_models} color="bg-secondary-100 text-secondary-600" />
                  </div>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-secondary-500 mb-1.5">核心验证指标</div>
                    <Chips items={p.core_metrics} color="bg-accent-50 text-accent-700" />
                  </div>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-green-600 mb-1.5">适合角色</div>
                    <Chips items={p.suitable_role_types} color="bg-green-50 text-green-700" />
                  </div>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-red-500 mb-1.5">不适合角色</div>
                    <Chips items={p.unsuitable_role_types} color="bg-red-50 text-red-600" />
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-[11px] font-medium uppercase tracking-wider text-amber-600 mb-1.5">风险规则</div>
                    <Chips items={p.risk_rules} color="bg-amber-50 text-amber-700" />
                  </div>
                </div>

                {sc && (
                  <div className="mt-4 pt-4 border-t border-secondary-100">
                    <div className="text-[11px] font-medium uppercase tracking-wider text-primary-600 mb-1.5">
                      素材格式 · {sc.asset_type_name}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                      <div>
                        <div className="text-[11px] text-secondary-500 mb-1">必填栏位</div>
                        <Chips items={sc.required_fields} color="bg-primary-50 text-primary-700" />
                      </div>
                      <div>
                        <div className="text-[11px] text-secondary-500 mb-1">禁止生成</div>
                        <Chips items={sc.must_not_generate} color="bg-red-50 text-red-600" />
                      </div>
                    </div>
                  </div>
                )}

                {sr && (
                  <div className="mt-4 pt-4 border-t border-secondary-100 flex flex-wrap items-center gap-4 text-xs text-foreground-600">
                    <span className="text-[11px] font-medium uppercase tracking-wider text-secondary-500">权重</span>
                    <span>曝光 <b className="text-foreground-900">{sr.exposure_weight}</b></span>
                    <span>互动 <b className="text-foreground-900">{sr.engagement_weight}</b></span>
                    <span>信任 <b className="text-foreground-900">{sr.trust_weight}</b></span>
                    <span>转化 <b className="text-foreground-900">{sr.conversion_weight}</b></span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
